const SYNC_CHANGE_EVENT = 'art-credits-sync-changed'

let syncCount = 0
let syncWaiters = []

function emitSyncChange() {
  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(
    new CustomEvent(SYNC_CHANGE_EVENT, {
      detail: { pending: syncCount > 0 },
    }),
  )
}

function beginSync() {
  syncCount += 1
  if (syncCount === 1) {
    emitSyncChange()
  }
}

function endSync() {
  syncCount = Math.max(0, syncCount - 1)

  if (syncCount === 0) {
    emitSyncChange()
    const waiters = syncWaiters
    syncWaiters = []
    waiters.forEach((resolve) => resolve())
  }
}

export function isArtCreditsSyncPending() {
  return syncCount > 0
}

export function subscribeArtCreditsSync(listener) {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handler = (event) => {
    listener(Boolean(event?.detail?.pending))
  }

  window.addEventListener(SYNC_CHANGE_EVENT, handler)
  listener(syncCount > 0)

  return () => {
    window.removeEventListener(SYNC_CHANGE_EVENT, handler)
  }
}

export async function waitForArtCreditsSync() {
  await new Promise((resolve) => {
    if (syncCount === 0) {
      resolve()
      return
    }

    syncWaiters.push(resolve)
  })
}

/**
 * Polls art credit availability until the total increases above the baseline
 * captured at start (webhook lag after package purchase).
 * Each caller polls independently so local UI state stays in sync.
 */
export async function pollArtCreditsUntilUpdated({
  refresh,
  getBaselineTotal = () => 0,
  maxAttempts = 12,
  intervalMs = 1500,
} = {}) {
  if (!refresh) {
    return { updated: false }
  }

  const baselineTotal = Number(getBaselineTotal() ?? 0)

  beginSync()

  try {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const availability = await refresh()
      const total = Number(availability?.total ?? 0)

      if (Number.isFinite(total) && total > baselineTotal) {
        return { updated: true, availability }
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => {
          setTimeout(resolve, intervalMs)
        })
      }
    }

    return { updated: false }
  } finally {
    endSync()
  }
}
